/**
 * Repository factory. `VITE_REPOSITORY=local` selects `LocalRepository`
 * (docs/ARCHITECTURE.md §5.6); anything else selects `SupabaseRepository`.
 *
 * The Supabase path needs `VITE_SUPABASE_URL` + `VITE_SUPABASE_ANON_KEY` in
 * `app/.env.local`. If they are missing, constructing the client throws — so the
 * factory falls back to `LocalRepository` with a loud warning rather than
 * leaving the app with no data layer at all. That keeps a fresh clone runnable
 * before anyone has copied `.env.local.example`.
 */

import { LocalRepository } from './LocalRepository.ts';
import type { LocalRepositoryOptions } from './LocalRepository.ts';
import type { Repository } from './Repository.ts';
import { SupabaseRepository } from './SupabaseRepository.ts';

export type RepositoryKind = 'local' | 'supabase';

export function repositoryKind(): RepositoryKind {
  const v = typeof import.meta.env === 'object' ? import.meta.env.VITE_REPOSITORY : undefined;
  return v === 'local' ? 'local' : 'supabase';
}

export function crewSlug(): string {
  const v = typeof import.meta.env === 'object' ? import.meta.env.VITE_CREW_SLUG : undefined;
  return v || 'alpha';
}

export function createRepository(opts: LocalRepositoryOptions = {}): Repository {
  if (repositoryKind() === 'local') return new LocalRepository(opts);
  try {
    return new SupabaseRepository();
  } catch (e) {
    console.error(
      '[qd] SupabaseRepository could not be constructed — falling back to LocalRepository. ' +
        'Copy app/.env.local.example to app/.env.local, or run `npm run dev:local`.',
      e,
    );
    return new LocalRepository(opts);
  }
}

export { LocalRepository } from './LocalRepository.ts';
export { SupabaseRepository } from './SupabaseRepository.ts';
export { RepoError, patchEchoKey } from './Repository.ts';
export type {
  Repository,
  CrewSnapshot,
  MyBoards,
  CrewPatch,
  CrewHandlers,
  PersonalHandlers,
} from './Repository.ts';
